import { ApiResponse } from "../../../types/api";
import apiClient from "../../apiClient";
import {
  CashInRecord,
  CashInRequest,
  CashOutRecord,
  CashOutRequest,
  CashInApiResponse,
  CashOutApiResponse,
  CashRecordsApiResponse,
  CashRecord,
} from "./type";

export const cashExchangeService = {
  createCashInByPlayerId: async (
    data: CashInRequest,
  ): Promise<ApiResponse<CashInRecord>> => {
    const response = await apiClient.post("/cash-in/by-player-id", data);
    return response.data;
  },

  createCashInByPlayerAndAgentId: async (
    data: CashInRequest,
  ): Promise<ApiResponse<CashInRecord>> => {
    const response = await apiClient.post(
      "/cash-in/by-player-and-agent-id",
      data,
    );
    return response.data;
  },

  getCashInsByPlayerId: async (
    playerId: string,
  ): Promise<ApiResponse<CashInRecord[]>> => {
    const response = await apiClient.get(`/cash-in/player/${playerId}`);
    return response.data;
  },

  getCashRecords: async (params?: {
    skip?: number;
    limit?: number;
    date?: string;
    fromDate?: string;
    toDate?: string;
    shift?: string;
    customerId?: string;
    agentId?: string;
    recordType?: "cash_in" | "cash_out" | "all";
    paymentType?: string;
    export?: boolean;
  }): Promise<CashRecordsApiResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined)
      queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined)
      queryParams.append("limit", params.limit.toString());
    if (params?.date) queryParams.append("date", params.date);
    if (params?.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params?.toDate) queryParams.append("toDate", params.toDate);
    if (params?.shift) queryParams.append("shift", params.shift);
    if (params?.customerId) queryParams.append("customerId", params.customerId);
    if (params?.agentId) queryParams.append("agentId", params.agentId);
    if (params?.recordType) queryParams.append("recordType", params.recordType);
    if (params?.paymentType)
      queryParams.append("paymentType", params.paymentType);
    if (params?.export) queryParams.append("export", params.export.toString());

    if (params?.export) {
      const response = await apiClient.get<ApiResponse<CashRecord[]>>(
        `/cash-records?${queryParams.toString()}`,
      );

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to export cash records",
        );
      }

      const records = response.data.data || [];
      return {
        items: records,
        pagination: {
          total: records.length,
          skip: 0,
          limit: records.length,
          returned: records.length,
        },
        filtersApplied: {
          recordType: params.recordType || "all",
          customerId: params.customerId || null,
          shift: params.shift || null,
          date: params.date || null,
          fromDate: params.fromDate || null,
          toDate: params.toDate || null,
        },
      };
    } else {
      const response = await apiClient.get<ApiResponse<CashRecordsApiResponse>>(
        `/cash-records?${queryParams.toString()}`,
      );
      return response.data.data;
    }
  },

  getCustomerCashRecords: async (
    customerId: string,
    params?: {
      skip?: number;
      limit?: number;
      date?: string;
      fromDate?: string;
      toDate?: string;
      shift?: string;
      recordType?: "cash_in" | "cash_out" | "all";
      paymentType?: string;
      export?: boolean;
    },
  ): Promise<CashRecordsApiResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined)
      queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined)
      queryParams.append("limit", params.limit.toString());
    if (params?.date) queryParams.append("date", params.date);
    if (params?.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params?.toDate) queryParams.append("toDate", params.toDate);
    if (params?.shift) queryParams.append("shift", params.shift);
    if (params?.recordType) queryParams.append("recordType", params.recordType);
    if (params?.paymentType)
      queryParams.append("paymentType", params.paymentType);
    if (params?.export) queryParams.append("export", params.export.toString());

    if (params?.export) {
      const response = await apiClient.get<ApiResponse<CashRecord[]>>(
        `/cash-records/customer/${customerId}?${queryParams.toString()}`,
      );

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to export customer cash records",
        );
      }

      const records = response.data.data || [];
      return {
        items: records,
        pagination: {
          total: records.length,
          skip: 0,
          limit: records.length,
          returned: records.length,
        },
        filtersApplied: {
          recordType: params.recordType || "all",
          customerId: customerId,
          shift: params.shift || null,
          date: params.date || null,
          fromDate: params.fromDate || null,
          toDate: params.toDate || null,
        },
      };
    } else {
      const response = await apiClient.get<ApiResponse<CashRecordsApiResponse>>(
        `/cash-records/customer/${customerId}?${queryParams.toString()}`,
      );
      return response.data.data;
    }
  },

  getCashIns: async (params?: {
    skip?: number;
    limit?: number;
    date?: string;
    fromDate?: string;
    toDate?: string;
    shift?: string;
    customerId?: string;
    paymentType?: string;
  }): Promise<CashInRecord[]> => {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined)
      queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined)
      queryParams.append("limit", params.limit.toString());
    if (params?.date) queryParams.append("date", params.date);
    if (params?.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params?.toDate) queryParams.append("toDate", params.toDate);
    if (params?.shift) queryParams.append("shift", params.shift);
    if (params?.customerId) queryParams.append("customerId", params.customerId);
    if (params?.paymentType)
      queryParams.append("paymentType", params.paymentType);

    const response = await apiClient.get<ApiResponse<CashInApiResponse>>(
      `/cash-in?${queryParams.toString()}`,
    );
    return response.data.data.items;
  },

  getCashInsByCustomerId: async (
    customerId: string,
    params?: {
      skip?: number;
      limit?: number;
      date?: string;
      fromDate?: string;
      toDate?: string;
      shift?: string;
    },
  ): Promise<CashInRecord[]> => {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined)
      queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined)
      queryParams.append("limit", params.limit.toString());
    if (params?.date) queryParams.append("date", params.date);
    if (params?.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params?.toDate) queryParams.append("toDate", params.toDate);
    if (params?.shift) queryParams.append("shift", params.shift);

    const response = await apiClient.get<ApiResponse<CashInApiResponse>>(
      `/cash-in/customer/${customerId}?${queryParams.toString()}`,
    );
    return response.data.data.items;
  },

  createCashOut: async (
    data: CashOutRequest,
  ): Promise<ApiResponse<CashOutRecord[]>> => {
    const response = await apiClient.post("/cash-out/by-player-id", data);
    return response.data;
  },

  getCashOutsByPlayerId: async (
    playerId: string,
  ): Promise<ApiResponse<CashOutRecord[]>> => {
    const response = await apiClient.get(`/cash-out/player/${playerId}`);
    return response.data;
  },

  getCashOutsByCustomerId: async (
    customerId: string,
    params?: {
      skip?: number;
      limit?: number;
      date?: string;
      fromDate?: string;
      toDate?: string;
      shift?: string;
    },
  ): Promise<CashOutRecord[]> => {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined)
      queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined)
      queryParams.append("limit", params.limit.toString());
    if (params?.date) queryParams.append("date", params.date);
    if (params?.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params?.toDate) queryParams.append("toDate", params.toDate);
    if (params?.shift) queryParams.append("shift", params.shift);

    const response = await apiClient.get<ApiResponse<CashOutApiResponse>>(
      `/cash-out/customer/${customerId}?${queryParams.toString()}`,
    );
    return response.data.data.items;
  },

  getCashOuts: async (params?: {
    skip?: number;
    limit?: number;
    date?: string;
    fromDate?: string;
    toDate?: string;
    shift?: string;
    customerId?: string;
  }): Promise<CashOutRecord[]> => {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined)
      queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined)
      queryParams.append("limit", params.limit.toString());
    if (params?.date) queryParams.append("date", params.date);
    if (params?.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params?.toDate) queryParams.append("toDate", params.toDate);
    if (params?.shift) queryParams.append("shift", params.shift);
    if (params?.customerId) queryParams.append("customerId", params.customerId);

    const response = await apiClient.get<ApiResponse<CashOutApiResponse>>(
      `/cash-out?${queryParams.toString()}`,
    );
    return response.data.data.items;
  },
};
